import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { downloadCsv } from "@/lib/download";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Download, Trash2, ArrowLeft, ArrowRight, Search,
  CheckCircle2, XCircle, Clock, AlertTriangle, RotateCcw, Pencil, Save, X,
  FileSpreadsheet, Plus, Send,
} from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { AddToProspectingDialog } from "@/components/Crm/AddToProspectingDialog";

const PAGE_SIZE = 20;

type BatchKind = "import" | "export";
type BatchStatus = "pending" | "processing" | "completed" | "failed" | "reverted";

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string; icon: any }> = {
    pending: { label: "Pendente", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: Clock },
    processing: { label: "Processando", cls: "bg-blue-100 text-blue-700 border-blue-300", icon: Clock },
    completed: { label: "Concluído", cls: "bg-green-100 text-green-700 border-green-300", icon: CheckCircle2 },
    failed: { label: "Falhou", cls: "bg-red-100 text-red-700 border-red-300", icon: XCircle },
    reverted: { label: "Excluído", cls: "bg-amber-100 text-amber-700 border-amber-300", icon: RotateCcw },
  };
  const cfg = map[status] ?? map.pending;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`text-xs ${cfg.cls} flex items-center gap-1 w-fit`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </Badge>
  );
}

function formatDate(d: Date | string | null | undefined) {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function batchLabel(b: any): string {
  return b.displayName || b.fileName || `Lote #${b.id}`;
}

export default function ImportHistoryTab() {
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<BatchKind | "all">("all");
  const [statusFilter, setStatusFilter] = useState<BatchStatus | "all">("all");
  const [page, setPage] = useState(0);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [downloadingBatchId, setDownloadingBatchId] = useState<number | null>(null);

  const utils = trpc.useUtils();
  const { data: tagsList = [] } = trpc.tags.list.useQuery();

  const { data, isLoading } = trpc.contacts.importsList.useQuery({
    kind: kindFilter === "all" ? undefined : kindFilter,
    status: statusFilter === "all" ? undefined : statusFilter,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const handleDownloadInvalid = async (batchId: number) => {
    setDownloadingBatchId(batchId);
    try {
      const res = await utils.contacts.importsDownloadInvalid.fetch({ batchId });
      downloadCsv(res.csv, res.fileName);
    } catch (e: any) {
      toast.error(toUserFriendlyError(e, "Falha ao baixar inválidos"));
    } finally {
      setDownloadingBatchId(null);
    }
  };

  const batches = (data?.batches as any[]) ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome de arquivo..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                className="pl-9"
              />
            </div>
            <Select value={kindFilter} onValueChange={(v) => { setKindFilter(v as any); setPage(0); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="import">Importação</SelectItem>
                <SelectItem value="export">Exportação</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as any); setPage(0); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="completed">Concluído</SelectItem>
                <SelectItem value="processing">Processando</SelectItem>
                <SelectItem value="failed">Falhou</SelectItem>
                <SelectItem value="reverted">Excluído</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="p-3 text-left font-medium">Data</th>
                  <th className="p-3 text-left font-medium">Lista</th>
                  <th className="p-3 text-left font-medium">Tipo</th>
                  <th className="p-3 text-left font-medium">Status</th>
                  <th className="p-3 text-right font-medium">Total</th>
                  <th className="p-3 text-right font-medium">Criados</th>
                  <th className="p-3 text-right font-medium">Atualizados</th>
                  <th className="p-3 text-right font-medium">Inválidos</th>
                  <th className="p-3 text-left font-medium">Tags</th>
                  <th className="p-3 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">Carregando...</td></tr>
                )}
                {!isLoading && batches.length === 0 && (
                  <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">
                    Nenhum lote encontrado.
                  </td></tr>
                )}
                {batches.map((b) => {
                  const appliedTags = (b.appliedTagIds ?? [])
                    .map((id: number) => (tagsList as any[]).find((t: any) => t.id === id))
                    .filter(Boolean);
                  return (
                    <tr
                      key={b.id}
                      className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => setSelectedBatchId(b.id)}
                    >
                      <td className="p-3 whitespace-nowrap">{formatDate(b.createdAt)}</td>
                      <td className="p-3 max-w-[260px] truncate" title={batchLabel(b)}>
                        <span className="font-medium">{batchLabel(b)}</span>
                        {b.displayName && b.fileName && (
                          <span className="text-xs text-muted-foreground block truncate">{b.fileName}</span>
                        )}
                      </td>
                      <td className="p-3">
                        <Badge variant={b.kind === "export" ? "secondary" : "default"} className="text-xs">
                          {b.kind === "export" ? "Exportação" : "Importação"}
                        </Badge>
                      </td>
                      <td className="p-3"><StatusBadge status={b.status} /></td>
                      <td className="p-3 text-right tabular-nums">{b.totalRows}</td>
                      <td className="p-3 text-right tabular-nums text-green-700">{b.createdCount}</td>
                      <td className="p-3 text-right tabular-nums text-blue-700">{b.updatedCount}</td>
                      <td className="p-3 text-right tabular-nums text-red-700">{b.invalidCount}</td>
                      <td className="p-3">
                        {appliedTags.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[160px]">
                            {appliedTags.slice(0, 3).map((tag: any) => (
                              <span
                                key={tag.id}
                                className="inline-block px-2 py-0.5 rounded-full text-xs font-medium border"
                                style={{
                                  borderLeft: `3px solid ${tag.color || "#3b82f6"}`,
                                  background: `${tag.color || "#3b82f6"}15`,
                                }}
                              >
                                {tag.name}
                              </span>
                            ))}
                            {appliedTags.length > 3 && (
                              <span className="text-xs text-muted-foreground">+{appliedTags.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          {b.invalidCount > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Baixar inválidos para correção"
                              disabled={downloadingBatchId === b.id}
                              onClick={() => handleDownloadInvalid(b.id)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Paginação */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages} — {total} lotes
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(p => Math.max(0, p - 1))}
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
            >
              Próxima <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Sheet de detalhes */}
      <BatchDetailSheet
        batchId={selectedBatchId}
        tagsList={tagsList as any[]}
        onClose={() => setSelectedBatchId(null)}
      />
    </div>
  );
}

function BatchDetailSheet({
  batchId,
  tagsList,
  onClose,
}: {
  batchId: number | null;
  tagsList: any[];
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.contacts.importsGet.useQuery(
    { batchId: batchId ?? 0 },
    { enabled: batchId !== null },
  );

  const batch = data?.batch as any;
  const invalidItems = (data?.invalidItems as any[]) ?? [];

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const { can } = usePermissions();
  const [showProspecting, setShowProspecting] = useState(false);

  // Sincroniza draft com o batch carregado
  useEffect(() => {
    if (batch) {
      setNameDraft(batch.displayName || batch.fileName || "");
      setEditingName(false);
    }
  }, [batch?.id, batch?.displayName, batch?.fileName]);

  const renameMut = trpc.contacts.importsRename.useMutation({
    onSuccess: () => {
      toast.success("Lista renomeada");
      utils.contacts.importsList.invalidate();
      utils.contacts.importsGet.invalidate();
      setEditingName(false);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const revertMut = trpc.contacts.importsRevert.useMutation({
    onSuccess: (res) => {
      toast.success(`Lista excluída. ${res.deletedContacts} contatos removidos.`);
      utils.contacts.importsList.invalidate();
      utils.contacts.list.invalidate();
      utils.contacts.getTagCounts.invalidate();
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // Editor de tags da lista
  const [editingTags, setEditingTags] = useState(false);
  const [tagDraft, setTagDraft] = useState<number[]>([]);
  useEffect(() => {
    if (batch) {
      setTagDraft(((batch.appliedTagIds ?? []) as number[]));
      setTagSearch("");
      setCreatingTag(false);
    }
  }, [batch?.id, batch?.appliedTagIds]);

  const invalidateTagViews = () => {
    utils.contacts.importsList.invalidate();
    utils.contacts.importsGet.invalidate();
    utils.contacts.list.invalidate();
    utils.contacts.getTagCounts.invalidate();
  };

  const applyTagsMut = trpc.contacts.importsApplyTags.useMutation({
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const removeTagsMut = trpc.contacts.importsRemoveTags.useMutation({
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // Confirmação de remoção (warning explícito)
  const [pendingRemoveIds, setPendingRemoveIds] = useState<number[] | null>(null);

  // Busca + criar nova tag inline
  const [tagSearch, setTagSearch] = useState("");
  const [creatingTag, setCreatingTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#3b82f6");
  // IDs de tags recém-criadas/adicionadas pra animar a entrada
  const [recentlyAddedIds, setRecentlyAddedIds] = useState<Set<number>>(new Set());

  // Filtragem por nome (case-insensitive, sem acento opcional)
  const normalizedSearch = tagSearch.trim().toLowerCase();
  const filteredTags = normalizedSearch
    ? (tagsList as any[]).filter((t) => (t.name ?? "").toLowerCase().includes(normalizedSearch))
    : (tagsList as any[]);
  const exactMatch = (tagsList as any[]).some(
    (t) => (t.name ?? "").toLowerCase() === normalizedSearch,
  );
  const showCreateFromSearch = normalizedSearch.length > 0 && filteredTags.length === 0 && !exactMatch;

  const createTagMut = trpc.tags.create.useMutation({
    onSuccess: (created: any) => {
      utils.tags.list.invalidate();
      const id = created?.id;
      if (typeof id !== "number") {
        toast.warning("Tag criada, mas o ID não retornou. Recarregue e marque manualmente.");
        setNewTagName("");
        setCreatingTag(false);
        setTagSearch("");
        return;
      }
      toast.success(`Tag "${created.name}" criada.`);
      setTagDraft((prev) => Array.from(new Set([...prev, id])));
      setRecentlyAddedIds((prev) => new Set(prev).add(id));
      setNewTagName("");
      setCreatingTag(false);
      setTagSearch("");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const handleCreateTag = () => {
    const name = newTagName.trim();
    if (!name) {
      toast.warning("Informe o nome da tag.");
      return;
    }
    createTagMut.mutate({ name, color: newTagColor });
  };

  const handleSaveTags = async () => {
    if (!batch) return;
    const current = ((batch.appliedTagIds ?? []) as number[]);
    const toAdd = tagDraft.filter((id) => !current.includes(id));
    const toRemove = current.filter((id) => !tagDraft.includes(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      setEditingTags(false);
      return;
    }

    // Remoção exige confirmação adicional (afeta TODOS contatos do lote,
    // mesmo os que receberam a tag por outras vias).
    if (toRemove.length > 0 && pendingRemoveIds === null) {
      setPendingRemoveIds(toRemove);
      return;
    }

    try {
      if (toAdd.length > 0) {
        const r = await applyTagsMut.mutateAsync({ batchId: batch.id, tagIds: toAdd });
        toast.success(`${r.inserted} associaçõe${r.inserted === 1 ? "" : "s"} adicionada${r.inserted === 1 ? "" : "s"}.`);
      }
      if (toRemove.length > 0) {
        const r = await removeTagsMut.mutateAsync({ batchId: batch.id, tagIds: toRemove });
        toast.success(`${r.removed} associaçõe${r.removed === 1 ? "" : "s"} removida${r.removed === 1 ? "" : "s"}.`);
      }
      invalidateTagViews();
      setEditingTags(false);
      setPendingRemoveIds(null);
    } catch {
      // toasts de erro já feitos pelos onError de cada mutation
    }
  };

  const [exporting, setExporting] = useState(false);
  const handleExportBatch = async () => {
    if (!batch) return;
    setExporting(true);
    try {
      const res = await utils.contacts.importsExportBatch.fetch({ batchId: batch.id });
      if (res.total === 0) {
        toast.info("Esta lista não tem contatos para exportar.");
      } else {
        downloadCsv(res.csv, res.fileName);
        toast.success(`${res.total} contatos exportados`);
      }
    } catch (e: any) {
      toast.error(toUserFriendlyError(e, "Falha ao exportar"));
    } finally {
      setExporting(false);
    }
  };

  const handleSaveName = () => {
    if (!batch) return;
    const trimmed = nameDraft.trim();
    if (trimmed === (batch.displayName || batch.fileName || "")) {
      setEditingName(false);
      return;
    }
    renameMut.mutate({ batchId: batch.id, displayName: trimmed });
  };

  const appliedTags = batch
    ? ((batch.appliedTagIds ?? []) as number[])
        .map(id => tagsList.find(t => t.id === id))
        .filter(Boolean)
    : [];

  const canRevert = batch?.kind === "import" && batch?.status === "completed";
  const isReverted = batch?.status === "reverted";

  return (
    <Sheet open={batchId !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Detalhes da Lista</SheetTitle>
          <SheetDescription>
            Informações, tags aplicadas, contatos inválidos e ações.
          </SheetDescription>
        </SheetHeader>

        {isLoading && <p className="text-sm text-muted-foreground mt-4">Carregando...</p>}

        {batch && (
          <div className="space-y-4 mt-4">
            {/* Nome editável */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <Label className="text-xs text-muted-foreground">Nome da lista</Label>
                {editingName ? (
                  <div className="flex gap-2">
                    <Input
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); handleSaveName(); }
                        if (e.key === "Escape") setEditingName(false);
                      }}
                      autoFocus
                      maxLength={200}
                    />
                    <Button size="sm" onClick={handleSaveName} disabled={renameMut.isPending}>
                      <Save className="h-4 w-4 mr-1" /> Salvar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
                    <p className="font-medium flex-1 truncate">{batchLabel(batch)}</p>
                    <Button size="sm" variant="ghost" onClick={() => setEditingName(true)} title="Renomear">
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                {batch.displayName && batch.fileName && (
                  <p className="text-xs text-muted-foreground">
                    Arquivo original: <span className="font-mono">{batch.fileName}</span>
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Metadados */}
            <Card>
              <CardContent className="p-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Tipo</p>
                  <p className="font-medium">{batch.kind === "export" ? "Exportação" : "Importação"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Status</p>
                  <StatusBadge status={batch.status} />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Estratégia</p>
                  <p className="font-medium">{batch.duplicateStrategy ?? "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Total no arquivo</p>
                  <p className="font-medium">{batch.totalRows}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Iniciado</p>
                  <p className="font-medium">{formatDate(batch.startedAt)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Concluído</p>
                  <p className="font-medium">{formatDate(batch.completedAt)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Tags aplicadas */}
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Tags aplicadas a esta lista</Label>
                  {!editingTags && !isReverted && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs gap-1.5"
                      onClick={() => setEditingTags(true)}
                    >
                      <Pencil className="h-3 w-3" />
                      {appliedTags.length === 0 ? "Adicionar" : "Editar"}
                    </Button>
                  )}
                </div>

                {!editingTags && (
                  appliedTags.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">Nenhuma tag foi aplicada.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {appliedTags.map((tag: any) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border"
                          style={{
                            borderLeft: `3px solid ${tag.color || "#3b82f6"}`,
                            background: `${tag.color || "#3b82f6"}15`,
                          }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )
                )}

                {editingTags && (
                  <div className="space-y-3 pt-1">
                    <p className="text-xs text-muted-foreground">
                      Marque as tags que deseja aplicar a todos os {batch.createdCount + batch.updatedCount} contatos desta lista.
                      Desmarcar uma tag aplicada remove ela dos contatos.
                    </p>

                    <div className="border rounded-md bg-muted/30">
                      {/* Busca */}
                      <div className="p-2 border-b">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                          <Input
                            value={tagSearch}
                            onChange={(e) => setTagSearch(e.target.value)}
                            placeholder="Buscar tag…"
                            className="h-7 pl-7 text-xs bg-background"
                          />
                          {tagSearch && (
                            <button
                              type="button"
                              onClick={() => setTagSearch("")}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              aria-label="Limpar busca"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Lista de tags */}
                      {tagsList.length === 0 && !creatingTag ? (
                        <p className="text-xs text-muted-foreground italic p-3 text-center">Nenhuma tag cadastrada ainda.</p>
                      ) : showCreateFromSearch ? (
                        <div className="p-3 text-center space-y-2">
                          <p className="text-xs text-muted-foreground">
                            Nenhuma tag encontrada com "<strong>{tagSearch}</strong>".
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs gap-1.5"
                            onClick={() => {
                              setNewTagName(tagSearch);
                              setCreatingTag(true);
                              setTagSearch("");
                            }}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Criar tag "{tagSearch}"
                          </Button>
                        </div>
                      ) : (
                        <div className="max-h-48 overflow-y-auto space-y-1 p-2">
                          {filteredTags.map((tag: any) => {
                            const checked = tagDraft.includes(tag.id);
                            const wasApplied = ((batch.appliedTagIds ?? []) as number[]).includes(tag.id);
                            const willRemove = wasApplied && !checked;
                            const justAdded = recentlyAddedIds.has(tag.id);
                            return (
                              <label
                                key={tag.id}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-background transition-all duration-300 ${
                                  willRemove ? "bg-red-50" : ""
                                } ${
                                  justAdded ? "bg-emerald-50 ring-1 ring-emerald-300 animate-in fade-in slide-in-from-left-2" : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    setTagDraft((prev) =>
                                      e.target.checked
                                        ? Array.from(new Set([...prev, tag.id]))
                                        : prev.filter((id) => id !== tag.id),
                                    );
                                    if (e.target.checked) {
                                      setRecentlyAddedIds((prev) => new Set(prev).add(tag.id));
                                      // Limpa o highlight após 2s
                                      setTimeout(() => {
                                        setRecentlyAddedIds((prev) => {
                                          const next = new Set(prev);
                                          next.delete(tag.id);
                                          return next;
                                        });
                                      }, 2000);
                                    }
                                  }}
                                  className="rounded"
                                />
                                <span
                                  className="inline-block w-2 h-2 rounded-full"
                                  style={{ background: tag.color || "#3b82f6" }}
                                />
                                <span className="flex-1">{tag.name}</span>
                                {justAdded && !wasApplied && (
                                  <span className="text-[10px] text-emerald-700 font-medium">✨ adicionada</span>
                                )}
                                {willRemove && (
                                  <span className="text-[10px] text-red-700 font-medium">será removida</span>
                                )}
                                {wasApplied && checked && !justAdded && (
                                  <span className="text-[10px] text-muted-foreground">aplicada</span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {/* Bloco de criar nova tag */}
                      <div className="border-t bg-background/50 p-2">
                        {creatingTag ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={newTagColor}
                              onChange={(e) => setNewTagColor(e.target.value)}
                              className="h-7 w-9 rounded border cursor-pointer"
                              title="Cor da tag"
                            />
                            <Input
                              autoFocus
                              value={newTagName}
                              onChange={(e) => setNewTagName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); handleCreateTag(); }
                                if (e.key === "Escape") { setCreatingTag(false); setNewTagName(""); }
                              }}
                              placeholder="Nome da nova tag"
                              className="h-7 text-xs flex-1"
                              maxLength={100}
                              disabled={createTagMut.isPending}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => { setCreatingTag(false); setNewTagName(""); }}
                              disabled={createTagMut.isPending}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              className="h-7 text-xs gap-1"
                              onClick={handleCreateTag}
                              disabled={createTagMut.isPending || !newTagName.trim()}
                            >
                              {createTagMut.isPending ? "Criando…" : "Criar"}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 w-full justify-start text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                            onClick={() => setCreatingTag(true)}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Criar nova tag
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingTags(false);
                          setTagDraft(((batch.appliedTagIds ?? []) as number[]));
                        }}
                        disabled={applyTagsMut.isPending || removeTagsMut.isPending}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveTags}
                        disabled={applyTagsMut.isPending || removeTagsMut.isPending || tagsList.length === 0}
                        className="gap-1.5"
                      >
                        <Save className="h-3.5 w-3.5" />
                        {(applyTagsMut.isPending || removeTagsMut.isPending) ? "Salvando…" : "Salvar"}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Confirmação de remoção — afeta contatos que receberam a tag por outras vias também */}
                <AlertDialog
                  open={pendingRemoveIds !== null}
                  onOpenChange={(open) => { if (!open) setPendingRemoveIds(null); }}
                >
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remover tags dos contatos?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {pendingRemoveIds && (
                          <>
                            Você está removendo {pendingRemoveIds.length} tag(s) dos contatos desta lista.
                            <br /><br />
                            Se um contato tem a mesma tag aplicada por <strong>outra lista ativa</strong>, a tag é <strong>mantida</strong> nele. Esta ação não pode ser desfeita.
                          </>
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => setPendingRemoveIds(null)}>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleSaveTags()}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Remover
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>

            {/* Contadores */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">{batch.createdCount}</p>
                <p className="text-xs text-green-600">Criados</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-700">{batch.updatedCount}</p>
                <p className="text-xs text-blue-600">Atualizados</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-700">{batch.skippedCount}</p>
                <p className="text-xs text-amber-600">Ignorados</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-700">{batch.invalidCount}</p>
                <p className="text-xs text-red-600">Inválidos</p>
              </div>
            </div>

            {batch.errorSummary && batch.errorSummary.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <p className="text-sm font-medium">Resumo de erros</p>
                  </div>
                  <div className="max-h-[160px] overflow-y-auto space-y-1">
                    {batch.errorSummary.map((err: string, i: number) => (
                      <p key={i} className="text-xs text-muted-foreground">{err}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {invalidItems.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm font-medium mb-3">Linhas inválidas ({invalidItems.length})</p>
                  <div className="max-h-[300px] overflow-y-auto border rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Linha</th>
                          <th className="p-2 text-left">Telefone</th>
                          <th className="p-2 text-left">Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invalidItems.map((it) => (
                          <tr key={it.id} className="border-t">
                            <td className="p-2">{it.rowNumber}</td>
                            <td className="p-2 font-mono">{it.phoneNumber ?? "—"}</td>
                            <td className="p-2 text-red-600">{it.errorMessage}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ações */}
            {batch.kind === "import" && !isReverted && (
              <Card>
                <CardContent className="p-4 space-y-3">
                  <Label className="text-xs text-muted-foreground">Ações desta lista</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportBatch}
                      disabled={exporting || batch.createdCount === 0}
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      {exporting ? "Exportando..." : "Exportar contatos da lista"}
                    </Button>

                    {can("crm:prospecting_view") && (
                      <Button variant="outline" size="sm" onClick={() => setShowProspecting(true)}>
                        <Send className="h-4 w-4 mr-1.5" /> Adicionar à prospecção
                      </Button>
                    )}

                    {canRevert && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                          >
                            <Trash2 className="h-4 w-4 mr-1.5" /> Excluir lista
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir esta lista?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Vai <strong>remover permanentemente</strong> os {batch.createdCount} contatos
                              criados por esta lista, junto com tags e variáveis associadas a eles.
                              <br /><br />
                              As <strong>atualizações</strong> feitas em contatos pré-existentes
                              por esta lista <strong>não</strong> serão desfeitas.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() => revertMut.mutate({ batchId: batch.id })}
                            >
                              Sim, excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </SheetContent>
      <AddToProspectingDialog
        open={showProspecting}
        onClose={() => setShowProspecting(false)}
        target={batchId != null ? { importBatchId: batchId } : null}
        count={batch ? (batch.createdCount ?? 0) + (batch.updatedCount ?? 0) : undefined}
      />
    </Sheet>
  );
}
